import {
  ColorsV2,
  ModalAction,
  ModalBody,
  ModalFooter,
  ModalFooterActions,
  ModalHeader,
  ModalV2,
  Text,
  Flex,
  TextInput,
  CopyButton,
  Skeleton,
  spaces,
  sizes,
} from '@electricjs/arc';
import { useTranslation } from 'react-i18next';

import { useGlobalUI } from '@/components/GlobalUIProvider';
import { useLazyGetSystemRecoveryKeyQuery } from '@/redux/slices/mdmManagementApiSlice';
import { useLockDeviceCompat } from '@/hooks/useLockDeviceCompat';
import { DeviceAsset } from '@/types/assets';
import { useEffect, useState } from 'react';

type LockDeviceModalProps = {
  organizationId: string;
  deviceAsset: DeviceAsset;
  employeeName: string;
  onClose: () => void;
};

const LockDeviceWithBitlockerModal = ({
  organizationId,
  deviceAsset,
  employeeName,
  onClose,
}: LockDeviceModalProps) => {
  const { t } = useTranslation('people');
  const { showSuccessToast, showErrorToast } = useGlobalUI();
  const [lockDevice, { isLoading }] = useLockDeviceCompat();
  const [recoveryKey, setRecoveryKey] = useState('');

  const [getRecoveryKey, { isFetching: isRecoveryKeyLoading }] =
    useLazyGetSystemRecoveryKeyQuery();

  const deviceId = deviceAsset.device?.id;
  const serialNumber = deviceAsset.serialNumber;

  useEffect(() => {
    const fetchRecoveryKey = async () => {
      if (
        !organizationId ||
        !deviceAsset.device?.id ||
        !deviceAsset.serialNumber
      )
        return;
      getRecoveryKey({
        organizationId,
        serialNumber: deviceAsset.serialNumber,
      })
        .unwrap()
        .then(result => {
          setRecoveryKey(result.recoveryKey);
        })
        .catch(error => {
          console.error('Failed to fetch recovery key:', error);
          showErrorToast({
            id: 'recovery-key-fetch-error-toast',
            title: t(
              'employeeProfile.modals.lockDeviceWithBitlocker.recoveryKeyNotAvailableTitle'
            ),
            message: t(
              'employeeProfile.modals.lockDeviceWithBitlocker.recoveryKeyNotAvailableMessage'
            ),
          });
        });
    };
    fetchRecoveryKey();
  }, [
    organizationId,
    deviceAsset.device?.id,
    deviceAsset.serialNumber,
    getRecoveryKey,
    showErrorToast,
    t,
  ]);

  const handleSubmit = async () => {
    try {
      await lockDevice({
        organizationId,
        deviceId,
        serialNumber,
        lockMethod: 'windows_bitlocker',
      }).unwrap();
      showSuccessToast({
        id: 'lock-device-success-toast',
        message: t(
          'employeeProfile.modals.lockDeviceWithBitlocker.successMessage'
        ),
      });
      onClose();
    } catch (error) {
      console.error('Error sending lock command: ', error);
      showErrorToast({
        id: 'lock-device-error-toast',
        message: t(
          'employeeProfile.modals.lockDeviceWithBitlocker.errorMessage'
        ),
      });
    }
  };

  return (
    <ModalV2 visible hide={onClose} ariaLabelledby="lock-device">
      <ModalHeader>
        <Text variant="heading-2">
          {employeeName
            ? t('employeeProfile.modals.lockDeviceWithBitlocker.title', {
                employeeName,
              })
            : t(
                'employeeProfile.modals.lockDeviceWithBitlocker.titleWithoutEmployee'
              )}
        </Text>
      </ModalHeader>
      <ModalBody>
        <Flex
          flexDirection="column"
          gap={spaces.space_l_rem}
          mb={spaces.space_xs_rem}>
          <Text variant="body">
            {t('employeeProfile.modals.lockDeviceWithBitlocker.instructions')}
          </Text>
          <Flex flexDirection="column" gap={spaces.space_s_rem}>
            <Text variant="label-small">
              {t(
                'employeeProfile.modals.lockDeviceWithBitlocker.recoveryKeyLabel'
              )}
            </Text>
            {isRecoveryKeyLoading ? (
              <Skeleton height={sizes.size_48} width="50rem" />
            ) : (
              <Flex alignItems="center" gap={spaces.space_s_rem}>
                <TextInput
                  id="recovery-key-input"
                  value={recoveryKey}
                  readOnly
                  minWidth="47rem"
                  style={{
                    fontFamily: 'monospace',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    height: sizes.size_48,
                    borderRadius: sizes.size_8,
                  }}
                />
                <CopyButton
                  id="copy-recovery-key-button"
                  textToCopy={recoveryKey}
                  icon="copy"
                  iconSize="medium"
                  successMsg={t(
                    'employeeProfile.modals.lockDeviceWithBitlocker.recoveryKeyCopied'
                  )}
                />
              </Flex>
            )}
          </Flex>
          <Text variant="body">
            {t(
              'employeeProfile.modals.lockDeviceWithBitlocker.recoveryKeyLocationInfo'
            )}
          </Text>
        </Flex>
      </ModalBody>
      <ModalFooter>
        <ModalFooterActions>
          <ModalAction id="lock-device-cancel" onClick={onClose} variant="text">
            {t('employeeProfile.modals.lockDeviceWithBitlocker.cancel')}
          </ModalAction>
          <ModalAction
            backgroundColor={ColorsV2.ERROR}
            id="lock-device-submit"
            onClick={handleSubmit}
            loading={isLoading}>
            {t('employeeProfile.modals.lockDeviceWithBitlocker.confirm')}
          </ModalAction>
        </ModalFooterActions>
      </ModalFooter>
    </ModalV2>
  );
};

export default LockDeviceWithBitlockerModal;
